// Picker de modelo para Interfaz V2.
//
// Es la UI que aparece al tocar el botón "Modelo" desde el ConfigurePanel.
// Tiene 3 sub-pantallas que comparten el mismo componente padre, navegadas
// con un mini state machine interno (`view`):
//
//   1. 'list'   — carousel horizontal con un tile de "Automático" sticky a
//                 la izquierda + modelos del catálogo mock. Debajo, dos
//                 botones: "Crea tu propio modelo" y "Cargá tu propio modelo".
//   2. 'create' — formulario de modelo personalizado (género/edad/cuerpo/
//                 piel/pelo/largo/descripción), basado en el modo
//                 personalizado de PruebaNuevaInterfaz.
//   3. 'upload' — pantalla intermedia con explicación y botón "Subir
//                 imagen de modelo".
//
// En mobile se renderiza dentro de un bottom sheet (portal a document.body
// para escapar de stacking contexts). En desktop se renderiza inline
// dentro del ConfigurePanel.

import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import {
  ArrowLeft,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  Sparkles,
  Upload,
  User,
  UserPlus,
  Users,
  X,
} from 'lucide-react';
import { type ModelV2, TIPO_CUERPO_EXTRA_GRANDE } from './modelsApi';
import { isHeicFile, isAvifFile, normalizeImageForUpload } from '../../utils/heicConvert';
import { AnimatedBlurBg } from './AnimatedBlurBg';
import CachedImg from '../../components/CachedImg';
import { HoverScrollCarousel } from './HoverScrollCarousel';
import { AddOptionCard } from './components/AddOptionCard';
import { useExitTransition } from '../../hooks/useExitTransition';

// Producto mínimo que el picker usa para mostrar los thumbnails de
// "Utilizando" en cada slot del modo multi. Coincide con MockProductV2.
export interface MultiPickerProduct {
  id: string;
  name: string;
  imageUrl: string;
}

// ─── Tipos ─────────────────────────────────────────────────────────────

export interface CustomModelFields {
  genero?: string;
  edad?: string;
  tipoCuerpo?: string;
  tonoPiel?: string;
  colorPelo?: string;
  largoPelo?: string;
  descripcion?: string;
}

export type ModelSelection =
  | { mode: 'auto' }
  | { mode: 'preset'; id: string; name: string; imageUrl: string }
  | { mode: 'custom'; fields: CustomModelFields }
  | { mode: 'upload'; previewUrl: string };

/** Label corto para mostrar en chips/botones del modelo seleccionado. */
export const modelSelectionLabel = (sel: ModelSelection | null): string => {
  if (!sel) return 'Sugerido';
  if (sel.mode === 'auto') return 'Automático';
  if (sel.mode === 'preset') return sel.name;
  if (sel.mode === 'custom') {
    const parts = [sel.fields.genero, sel.fields.edad].filter(Boolean);
    return parts.join(' · ') || 'Personalizado';
  }
  return 'Modelo propio';
};

// ─── Opciones del modelo personalizado (copiadas de PruebaNuevaInterfaz) ─

const GENERO_OPTIONS = ['Hombre', 'Mujer'];
const EDAD_OPTIONS = [
  'Bebé',
  'Niño',
  'Joven (25 años)',
  'Adulto/a (30 a 40 años)',
  'Mayor',
];
const TIPO_CUERPO_OPTIONS = [
  'Promedio',
  'Talle Grande',
  TIPO_CUERPO_EXTRA_GRANDE,
];
const SKIN_TONE_OPTIONS = [
  { label: 'Muy clara', color: '#FFDFC4' },
  { label: 'Clara', color: '#F0C8A0' },
  { label: 'Beige', color: '#DEB887' },
  { label: 'Trigueña', color: '#CD853F' },
  { label: 'Oscura', color: '#8B4513' },
  { label: 'Muy oscura', color: '#3C2414' },
];
const HAIR_COLOR_OPTIONS = [
  { label: 'Negro', color: '#232323' },
  { label: 'Castaño oscuro', color: '#4B2E09' },
  { label: 'Castaño claro', color: '#A0522D' },
  { label: 'Rubio', color: '#F8E49B' },
  { label: 'Colorado', color: '#B55239' },
];
const LARGO_PELO_OPTIONS = ['Rapado', 'Corto', 'Medio', 'Largo'];

// ─── Sub-componente: ListView (carousel + 2 buttons) ───────────────────

interface ListViewProps {
  /** Selección actual — para resaltar el tile correspondiente. */
  selection: ModelSelection | null;
  /** Modelos reales fetchados desde la DB (globales + privados del user). */
  models: ModelV2[];
  onSelectAuto: () => void;
  onSelectPreset: (m: ModelV2) => void;
  onTapCreate: () => void;
  onTapUpload: () => void;
  /** Si true, no se muestra el tile "Automático" (ej: al adjuntar un
   *  modelo desde el chat, donde "automático" no aplica). */
  hideAuto?: boolean;
  /** Opcional: callback para activar "Utilizar varios modelos". Si no se
   *  pasa, no se renderiza el CTA (e.g., cuando ya estás editando un
   *  slot del modo multi). */
  onTapMulti?: () => void;
  /**
   * 'sheet'  → mobile bottom sheet: cards GRANDES (aspect 9:16), carousel
   *           centrado vertical en el espacio restante, botones anclados
   *           al bottom.
   * 'inline' → desktop: cards más chicas (aspect 3:4), todo en flujo
   *           vertical normal.
   */
  variant: 'sheet' | 'inline';
}

const ListView: React.FC<ListViewProps> = ({
  selection,
  models,
  onSelectAuto,
  onSelectPreset,
  onTapCreate,
  onTapUpload,
  onTapMulti,
  hideAuto = false,
  variant,
}) => {
  const autoSelected = selection?.mode === 'auto';
  const selectedPresetId =
    selection?.mode === 'preset' ? selection.id : null;
  const isSheet = variant === 'sheet';

  // Para el orden del carrusel pineamos la id seleccionada al MOMENTO
  // del primer render del picker (e.g., al abrir el sheet o entrar a
  // configure en desktop). Si cambiara con cada tap, los modelos se
  // reordenarían en vivo y la UX se siente inestable.
  const pinnedIdRef = useRef<string | null>(selectedPresetId);

  // Tamaño + aspect según contexto. Ambos usan aspect 9:16 — las imágenes
  // de presentación de los modelos vienen en ese ratio desde el backend,
  // así que mostrarlas así evita cropping en thumbs.
  //   sheet  → 220px de ancho (~391px alto) — domina el sheet mobile.
  //   inline → 140px de ancho (~249px alto) — entra cómodo en el panel
  //           desktop sin ahogar los otros controles.
  const cardClass = isSheet
    ? 'shrink-0 w-[220px] aspect-[9/16]'
    : 'shrink-0 w-[140px] aspect-[9/16]';

  // Orden: primero los modelos guardados (propios de la empresa), después los
  // de Atlas (globales, idCompany === null). Dentro de cada grupo, el
  // seleccionado (último usado) primero y luego los más nuevos — el array ya
  // viene por created_at desc, así que el sort estable preserva ese orden.
  const sortedModels = [...models].sort((a, b) => {
    const aOwn = a.idCompany !== null ? 0 : 1;
    const bOwn = b.idCompany !== null ? 0 : 1;
    if (aOwn !== bOwn) return aOwn - bOwn;
    const aSel = pinnedIdRef.current === String(a.id) ? 0 : 1;
    const bSel = pinnedIdRef.current === String(b.id) ? 0 : 1;
    return aSel - bSel;
  });

  // Tile "Automático" — `cls` define la medida (carrusel: fija; grid:
  // fluida). `big` controla el tamaño de iconos/texto.
  const autoCard = (cls: string, big: boolean) => (
    <button
      type="button"
      onClick={onSelectAuto}
      className={`${cls} rounded-2xl overflow-hidden relative flex flex-col items-center justify-center gap-2.5 text-white shadow-sm transition-transform active:scale-[0.97] bg-brand-primary ${
        autoSelected ? 'ring-2 ring-brand-primary ring-offset-2' : ''
      }`}
    >
      <AnimatedBlurBg pauseOnDesktopUnselected selected={autoSelected} />
      <Sparkles
        className={`relative z-10 ${big ? 'w-9 h-9 drop-shadow' : 'w-7 h-7 drop-shadow'}`}
      />
      <span
        className={`relative z-10 ${big ? 'text-[15px]' : 'text-[13.5px]'} font-semibold leading-tight px-2 text-center drop-shadow`}
      >
        Automático
      </span>
    </button>
  );

  // Card de un modelo del catálogo.
  const modelCard = (m: ModelV2, cls: string, big: boolean) => {
    const idStr = String(m.id);
    const isSel = selectedPresetId === idStr;
    const isGlobal = m.idCompany === null;
    return (
      <button
        key={m.id}
        type="button"
        onClick={() => onSelectPreset(m)}
        // Las imágenes de modelo son full-res (768×1376 ≈ 4MB de textura GPU
        // c/u). Con muchos modelos, componerlas todas a la vez satura la GPU y
        // produce parpadeo/blancos. `content-visibility:auto` hace que el
        // navegador SOLO componga las cards visibles; las de fuera de pantalla
        // no consumen textura. `contain-intrinsic-size` (con `auto` que recuerda
        // el tamaño real ya renderizado) preserva el tamaño de scroll.
        style={{
          contentVisibility: 'auto',
          containIntrinsicSize: 'auto 200px auto 350px',
        } as React.CSSProperties}
        className={`${cls} rounded-2xl overflow-hidden bg-neutral-100 border-2 ${
          isSel ? 'border-brand-primary' : 'border-neutral-200'
        } transition-all relative active:scale-[0.97]`}
      >
        {m.thumbnailUrl && (
          <CachedImg
            src={m.thumbnailUrl}
            alt={m.nombre}
            loading="lazy"
            className="w-full h-full object-cover"
            onError={(e) => {
              e.currentTarget.style.visibility = 'hidden';
            }}
          />
        )}
        {isGlobal && (
          // Fondo SÓLIDO a propósito (sin `backdrop-blur`): el badge es lo único
          // que diferencia una card global, y el backdrop-blur re-rasterizaba al
          // seleccionar (cambia el fondo detrás → ring/check) provocando un
          // parpadeo visible solo en los globales. Sólido = sin re-raster.
          <span className="absolute top-1.5 left-1.5 inline-flex items-center gap-0.5 bg-black/65 text-white text-[9.5px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded-md">
            <Sparkles className="w-2.5 h-2.5" />
            Atlas
          </span>
        )}
        {isSel && (
          <span className="absolute top-2 right-2 w-7 h-7 rounded-full inline-flex items-center justify-center shadow-md ring-2 ring-white bg-brand-primary text-white">
            <Check className="w-4 h-4" strokeWidth={3} />
          </span>
        )}
        <div
          className={`absolute inset-x-0 bottom-0 ${big ? 'px-3 py-2.5' : 'px-2 py-1.5'} bg-gradient-to-t from-black/65 via-black/15 to-transparent`}
        >
          <p
            className={`text-left text-white ${big ? 'text-[15px]' : 'text-[13px]'} font-semibold leading-tight drop-shadow truncate`}
          >
            {m.nombre}
          </p>
        </div>
      </button>
    );
  };

  // Opciones de la card "Agregar modelo" (popover en el grid desktop).
  const addOptions = [
    {
      icon: <Sparkles className="w-3.5 h-3.5" />,
      label: 'Creá tu modelo',
      onClick: onTapCreate,
    },
    {
      icon: <UserPlus className="w-3.5 h-3.5" />,
      label: 'Cargá tu propio modelo',
      onClick: onTapUpload,
    },
    ...(onTapMulti
      ? [
          {
            icon: <Users className="w-3.5 h-3.5" />,
            label: 'Utilizar varios modelos',
            onClick: onTapMulti,
          },
        ]
      : []),
  ];

  const carousel = (
    <HoverScrollCarousel
      // `py-2 -my-2`: el carrusel es `overflow-x-auto`, y por spec de CSS eso
      // fuerza `overflow-y` a `auto` → recortaba el `ring-offset-2` de la card
      // "Automático" arriba/abajo. El padding vertical le da aire al ring y el
      // margen negativo lo compensa para no mover el layout.
      className="flex gap-3 overflow-x-auto -mx-5 px-5 py-2 -my-2 scrollbar-hide"
      style={{
        scrollbarWidth: 'none',
        msOverflowStyle: 'none',
        WebkitOverflowScrolling: 'touch',
      }}
      arrowInset={4}
    >
      {!hideAuto && autoCard(cardClass, isSheet)}
      {sortedModels.map((m) => modelCard(m, cardClass, isSheet))}
    </HoverScrollCarousel>
  );

  // Grid desktop del sheet — 2 columnas, cards fluidas + card "Agregar".
  const desktopGrid = (
    <div className="h-full overflow-y-auto -mx-1 px-1 py-1">
      <div className="grid grid-cols-2 gap-3">
        {!hideAuto && autoCard('w-full aspect-[9/16]', true)}
        <AddOptionCard
          label="Agregar modelo"
          className="w-full aspect-[9/16]"
          options={addOptions}
        />
        {sortedModels.map((m) => modelCard(m, 'w-full aspect-[9/16]', true))}
      </div>
    </div>
  );

  const ctaButtons = (
    <div className="flex flex-col gap-2.5">
      <button
        type="button"
        onClick={onTapCreate}
        className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
      >
        <span className="inline-flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <Sparkles className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Creá tu modelo
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
      </button>
      <button
        type="button"
        onClick={onTapUpload}
        className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
      >
        <span className="inline-flex items-center gap-2.5">
          <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
            <UserPlus className="w-3.5 h-3.5 text-brand-primary" />
          </span>
          Cargá tu propio modelo
        </span>
        <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
      </button>
      {onTapMulti && (
        <button
          type="button"
          onClick={onTapMulti}
          className="w-full inline-flex items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors"
        >
          <span className="inline-flex items-center gap-2.5">
            <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
              <Users className="w-3.5 h-3.5 text-brand-primary" />
            </span>
            Utilizar varios modelos
          </span>
          <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
        </button>
      )}
    </div>
  );

  if (isSheet) {
    // Sheet: mobile mantiene el carrusel horizontal + CTAs abajo; desktop
    // pasa a un grid de 2 columnas con la card "Agregar modelo" (que abre
    // un popover con crear / subir / varios), sin los CTAs sueltos.
    return (
      <div className="flex flex-col h-full">
        {/* Mobile */}
        <div className="md:hidden flex flex-col h-full">
          <div className="flex-1 flex items-center min-h-0">{carousel}</div>
          <div className="shrink-0 pt-4">{ctaButtons}</div>
        </div>
        {/* Desktop */}
        <div className="hidden md:block h-full min-h-0">{desktopGrid}</div>
      </div>
    );
  }

  // Desktop inline — flujo normal con gap entre carousel y CTAs.
  return (
    <div className="flex flex-col gap-4">
      {carousel}
      {ctaButtons}
    </div>
  );
};

// ─── Sub-componente: MultiView (2 containers, mode "varios modelos") ──
// Reemplaza el carousel del ListView cuando el user activó "Utilizar
// varios modelos". Cada slot es una mini-card con: thumb del modelo
// (preset → imagen, auto → gradiente Sparkles, vacío → placeholder),
// nombre, label "Utilizando" y los thumbnails de los productos
// seleccionados. La X en la esquina superior derecha quita ese slot —
// si al sacarlo queda solo uno, el padre desactiva el modo multi.
//
// Tap en el área thumb/nombre → navega a la sub-list view para
// elegir/cambiar ese modelo (con back button "Atrás" en lugar de
// "Cancelar" porque es un sub-flow).
//
// Layout: vertical en mobile (`flex-col`), horizontal en desktop
// (`md:flex-row`). Cada slot ocupa el mismo espacio (`flex-1`).

interface MultiSlotProps {
  idx: number;
  selection: ModelSelection | null;
  products: MultiPickerProduct[];
  onTap: () => void;
  onRemove: () => void;
  /** Opcional: tap en "Utilizando" / miniaturas — abre el picker de
   *  productos para asignar específicamente a este slot. */
  onTapProducts?: () => void;
  /** Si true, oculta el botón X de "Quitar modelo". Usado por el flow
   *  de E-commerce donde la cantidad de slots viene fija por la pose. */
  lockRemove?: boolean;
}

const MultiSlot: React.FC<MultiSlotProps> = ({
  selection,
  products,
  onTap,
  onRemove,
  onTapProducts,
  lockRemove = false,
}) => {
  const isPreset = selection?.mode === 'preset';
  const isAuto = selection?.mode === 'auto';
  const isCustom = selection?.mode === 'custom';
  const thumbUrl = isPreset ? selection.imageUrl : null;
  const displayName = selection
    ? modelSelectionLabel(selection)
    : 'Elegí un modelo';

  // Thumb del modelo — reutilizado en ambos layouts. Acepta una clase
  // wrapper externa para fijar tamaño/aspect según el variant.
  const renderThumb = (wrapperClass: string) => (
    <div
      className={`${wrapperClass} bg-neutral-100 rounded-xl overflow-hidden relative shrink-0`}
    >
      {thumbUrl ? (
        <CachedImg
          src={thumbUrl}
          alt={displayName}
          className="absolute inset-0 w-full h-full object-cover object-top"
          // Si la signed URL caduca o falla, ocultamos la imagen rota
          // y queda el fondo neutral-100 del wrapper como placeholder.
          onError={(e) => {
            e.currentTarget.style.visibility = 'hidden';
          }}
        />
      ) : isAuto ? (
        <div className="absolute inset-0 bg-brand-primary flex items-center justify-center text-white">
          <AnimatedBlurBg selected />
          <Sparkles className="relative z-10 w-7 h-7 drop-shadow" />
        </div>
      ) : isCustom ? (
        <div className="absolute inset-0 bg-gradient-to-br from-brand-primary/15 to-brand-primary/5 flex items-center justify-center text-brand-primary">
          <User className="w-8 h-8" strokeWidth={1.5} />
        </div>
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-1.5 text-[#3C3C43]/40">
          <UserPlus className="w-7 h-7" strokeWidth={1.5} />
          <span className="text-[11.5px] font-medium px-2 text-center">
            Toca para elegir
          </span>
        </div>
      )}
    </div>
  );

  const productsRowInner = (
    <>
      <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
        Utilizando
      </p>
      {products.length === 0 ? (
        <p className="text-[12.5px] text-[#3C3C43]/45 italic">
          Sin productos seleccionados
        </p>
      ) : (
        <div className="flex items-center -space-x-2">
          {products.slice(0, 4).map((p) => (
            <CachedImg
              key={p.id}
              src={p.imageUrl}
              alt={p.name}
              className="w-9 h-9 rounded-lg object-cover ring-2 ring-white shadow-sm"
            />
          ))}
          {products.length > 4 && (
            <div className="w-9 h-9 rounded-lg bg-neutral-100 ring-2 ring-white flex items-center justify-center text-[11px] font-semibold text-brand-black/70 shadow-sm">
              +{products.length - 4}
            </div>
          )}
        </div>
      )}
    </>
  );
  const productsRow = onTapProducts ? (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        onTapProducts();
      }}
      className="flex flex-col items-center gap-2 rounded-xl px-2 py-1 -mx-2 -my-1 hover:bg-neutral-50 active:bg-neutral-100 transition-colors"
    >
      {productsRowInner}
    </button>
  ) : (
    <div className="flex flex-col items-center gap-2">{productsRowInner}</div>
  );

  return (
    <div className="flex-1 md:flex-none md:w-[280px] md:shrink-0 relative rounded-2xl border border-neutral-200 bg-white overflow-hidden animate-mode-swap">
      {/* X — esquina superior derecha. Pisamos el onClick del wrapper
          con stopPropagation para que no abra el sub-picker. Se oculta
          cuando `lockRemove` (flow E-commerce con cantidad fija de
          modelos por la pose). */}
      {!lockRemove && (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Quitar modelo"
          className="absolute top-2.5 right-2.5 z-20 w-7 h-7 inline-flex items-center justify-center rounded-full bg-black/55 backdrop-blur-sm text-white hover:bg-black/70 active:scale-95 transition-all"
        >
          <X className="w-3.5 h-3.5" strokeWidth={2.5} />
        </button>
      )}

      {/* Mismo layout en mobile y desktop: imagen izq (mitad ancho del
          slot) + columna derecha centrada con nombre arriba y
          "Utilizando" + thumbnails debajo. En desktop los slots se
          disponen en `flex-row` desde MultiView, así quedan uno al
          lado del otro.
          El `<button onTap>` envuelve solo thumb + nombre (no los
          productos), porque productsRow tiene su propio button cuando
          `onTapProducts` está provisto (HTML no permite nested
          buttons). Para el área productsRow sin tap, también lo
          dejamos como hermano por uniformidad. */}
      <div className="flex items-stretch gap-3 p-3">
        <button
          type="button"
          onClick={onTap}
          className="shrink-0 w-1/2 active:opacity-70 transition-opacity"
        >
          {renderThumb('w-full aspect-[3/4] md:aspect-[9/16]')}
        </button>
        <div className="flex-1 min-w-0 flex flex-col items-center justify-center gap-6">
          <button
            type="button"
            onClick={onTap}
            className="text-center w-full active:opacity-70 transition-opacity"
          >
            <p
              className={`text-[15px] font-semibold leading-snug ${
                selection ? 'text-brand-black/90' : 'text-[#3C3C43]/55'
              }`}
            >
              {displayName}
            </p>
          </button>
          {productsRow}
        </div>
      </div>
    </div>
  );
};

interface MultiViewProps {
  /** Slots de modelos (entre 2 y 4). El primero es el slot principal,
   *  los siguientes son extras. */
  slots: (ModelSelection | null)[];
  /** Productos default — usado cuando un slot no tiene asignación
   *  propia en `productsPerSlot`. */
  products: MultiPickerProduct[];
  /** Opcional: productos específicos por slot (idx = posición). Si un
   *  slot no aparece en este record, cae al `products` default. */
  productsPerSlot?: Record<number, MultiPickerProduct[]>;
  onTapSlot: (idx: number) => void;
  onRemoveSlot: (idx: number) => void;
  /** Opcional: tap en "Utilizando"/miniaturas de un slot — abre el
   *  picker de productos para ese slot. */
  onTapSlotProducts?: (idx: number) => void;
  /** Callback para añadir un slot vacío al final (hasta 4 max). Si no
   *  se pasa o no quedan slots disponibles, no se renderiza el CTA. */
  onAddSlot?: () => void;
  /** Tope de slots permitidos. Default 4. */
  maxSlots?: number;
  /** Si true, oculta el botón "Añadir modelo" y los botones X de cada
   *  slot — la cantidad queda fija. Usado por E-commerce donde la pose
   *  define cuántos modelos van. */
  lockSlotCount?: boolean;
}

const MultiView: React.FC<MultiViewProps> = ({
  slots,
  products,
  productsPerSlot,
  onTapSlot,
  onRemoveSlot,
  onTapSlotProducts,
  onAddSlot,
  maxSlots = 3,
  lockSlotCount = false,
}) => {
  const canAddMore =
    !lockSlotCount && !!onAddSlot && slots.length < maxSlots;
  return (
    <div className="flex flex-col gap-3">
      {/* Row de containers: vertical en mobile, horizontal con scroll
          en desktop. El CTA "Añadir modelo" vive afuera del row para
          poder tomar full-width en desktop. */}
      <div className="flex flex-col md:flex-row md:overflow-x-auto md:pb-2 gap-3 scrollbar-thin">
        {slots.map((sel, idx) => (
          <MultiSlot
            key={idx}
            idx={idx}
            selection={sel}
            products={productsPerSlot?.[idx] ?? products}
            onTap={() => onTapSlot(idx)}
            onRemove={() => onRemoveSlot(idx)}
            onTapProducts={
              onTapSlotProducts ? () => onTapSlotProducts(idx) : undefined
            }
            lockRemove={lockSlotCount}
          />
        ))}
      </div>
      {canAddMore && (
        <>
          {/* Mobile: pill compacto centrado abajo. */}
          <button
            type="button"
            onClick={onAddSlot}
            className="md:hidden self-center shrink-0 inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-neutral-100 hover:bg-neutral-200 active:bg-neutral-200 border border-neutral-200 text-[#3C3C43]/75 hover:text-[#3C3C43] text-[13.5px] font-medium transition-colors"
          >
            <Plus className="w-3.5 h-3.5" strokeWidth={2.5} />
            Añadir modelo
          </button>
          {/* Desktop: CTA full-width tipo "Creá tu modelo" / "Cargá
              tu propio modelo" — mismo estilo (rounded-2xl, icon-circle,
              chevron). */}
          <button
            type="button"
            onClick={onAddSlot}
            className="hidden md:inline-flex w-full items-center justify-between gap-2 py-3 px-4 rounded-2xl bg-white border border-neutral-200 hover:border-brand-primary/40 text-brand-black/85 text-[14.5px] font-medium transition-colors animate-mode-swap"
          >
            <span className="inline-flex items-center gap-2.5">
              <span className="w-7 h-7 rounded-full bg-brand-primary/10 inline-flex items-center justify-center">
                <Plus className="w-3.5 h-3.5 text-brand-primary" />
              </span>
              Añadir modelo
            </span>
            <ChevronRight className="w-4 h-4 text-[#3C3C43]/55" />
          </button>
        </>
      )}
    </div>
  );
};

// ─── Sub-componente: CollapsibleSection ────────────────────────────────
// Container con título + chevron a la derecha, que despliega los hijos al
// tocarse. La animación se hace con el truco de `grid-template-rows: 1fr`
// → `0fr` (transition-all sobre grid-rows), que permite animar la altura
// natural del contenido sin medir nada con JS. El chevron rota 180° al
// abrir.

interface CollapsibleSectionProps {
  title: string;
  /** Hint corto que se muestra al lado del título cuando está cerrado. */
  summary?: string | null;
  children: React.ReactNode;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({
  title,
  summary,
  children,
}) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-2xl border border-neutral-200 bg-white overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-2.5 px-4 py-3.5 active:bg-neutral-50 transition-colors"
        aria-expanded={open}
      >
        <span className="text-[14.5px] font-semibold text-brand-black/90 shrink-0">
          {title}
        </span>
        {summary && !open && (
          <span className="ml-auto text-[13px] text-[#3C3C43]/55 truncate text-right min-w-0">
            {summary}
          </span>
        )}
        <ChevronDown
          className={`w-4 h-4 text-brand-black/55 shrink-0 transition-transform duration-250 ${
            summary && !open ? 'ml-2' : 'ml-auto'
          } ${open ? 'rotate-180' : ''}`}
        />
      </button>
      <div
        className={`grid transition-all duration-300 ease-out ${
          open ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="px-4 pb-4 pt-1">{children}</div>
        </div>
      </div>
    </div>
  );
};

// ─── Sub-componente: CreateView (custom fields) ─────────────────────────
// Cada categoría es un CollapsibleSection acordeón. Empiezan cerrados, el
// user expande lo que quiere editar. Cuando hay valor seleccionado, se
// muestra como summary al lado del título.

interface CreateViewProps {
  initial?: CustomModelFields;
  onBack: () => void;
  /** id_auth del user — para el webhook procesar_modelo_propio (modo crear). */
  userId: string | null;
  /** id_company — para guardar el modelo al Utilizar. */
  companyId: string | null;
  /** Refresca la lista de modelos del padre tras guardar. */
  onModelCreated: () => void;
  /** El user creó + guardó el modelo (tras Utilizar) — vuelve el ModelV2. */
  onConfirmModel: (model: ModelV2) => void;
}

export const CreateView: React.FC<CreateViewProps> = ({
  initial,
  onBack,
  userId,
  companyId,
  onModelCreated,
  onConfirmModel,
}) => {
  // Fase del flow: 'form' (elegir características) → 'processing' (webhook
  // modo crear) → 'result' (preview + nombre + Utilizar). Misma estructura
  // que UploadFlow, reusando ModelProcessingView / ModelResultView.
  const [phase, setPhase] = useState<'form' | 'processing' | 'result'>('form');
  const [resultRefB64, setResultRefB64] = useState<string | null>(null);
  const [resultAnglesB64, setResultAnglesB64] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState<string>('');
  const [savingName, setSavingName] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [genero, setGenero] = useState<string>(initial?.genero || '');
  const [edad, setEdad] = useState<string>(initial?.edad || '');
  const [tipoCuerpo, setTipoCuerpo] = useState<string>(initial?.tipoCuerpo || '');
  const [tonoPiel, setTonoPiel] = useState<string>(initial?.tonoPiel || '');
  const [colorPelo, setColorPelo] = useState<string>(initial?.colorPelo || '');
  const [largoPelo, setLargoPelo] = useState<string>(initial?.largoPelo || '');
  const [descripcion, setDescripcion] = useState<string>(
    initial?.descripcion || '',
  );

  // Helper: chip button. Toggle-able (tap again to clear).
  const chip = (
    value: string,
    current: string,
    setter: (v: string) => void,
  ) => (
    <button
      key={value}
      type="button"
      onClick={() => setter(current === value ? '' : value)}
      className={`px-4 h-10 rounded-full border text-[14px] transition-colors ${
        current === value
          ? 'bg-brand-primary text-white border-brand-primary'
          : 'bg-white text-brand-black/85 border-neutral-200 hover:bg-neutral-50'
      }`}
    >
      {value}
    </button>
  );

  // Pill estilo PruebaNuevaInterfaz desktop (más grande, text-[15px], h-10).
  const dChip = (
    value: string,
    current: string,
    setter: (v: string) => void,
  ) => (
    <button
      key={value}
      type="button"
      onClick={() => setter(current === value ? '' : value)}
      className={`px-4 h-10 rounded-full border text-[15px] transition-colors ${
        current === value
          ? 'bg-brand-primary text-white border-brand-primary'
          : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
      }`}
    >
      {value}
    </button>
  );

  // Aceptar en el form → manda las características al webhook (modo crear) y
  // pasa a la fase de preview con el modelo generado.
  const handleSubmit = async () => {
    if (phase === 'processing') return;
    if (!userId) {
      setError('No pudimos identificar al usuario. Probá recargar.');
      return;
    }
    setError(null);
    setPhase('processing');
    try {
      const { createModelFromFields } = await import('./modelsApi');
      const { imageRefB64, imageAnglesB64 } = await createModelFromFields({
        userId,
        fields: {
          genero: genero || undefined,
          edad: edad || undefined,
          tipoCuerpo: tipoCuerpo || undefined,
          tonoPiel: tonoPiel || undefined,
          colorPelo: colorPelo || undefined,
          largoPelo: largoPelo || undefined,
          descripcion: descripcion || undefined,
        },
      });
      setResultRefB64(imageRefB64);
      setResultAnglesB64(imageAnglesB64);
      setNameInput('');
      setPhase('result');
    } catch (err) {
      console.error('[ModelPicker] createModelFromFields error', err);
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos crear el modelo. Probá de nuevo.',
      );
      setPhase('form');
    }
  };

  // Utilizar en el preview → guarda el modelo (storage + DB) y lo devuelve.
  const handleUtilizar = async () => {
    if (!resultRefB64 || !resultAnglesB64) return;
    if (!companyId) {
      setError('No pudimos identificar la empresa. Probá recargar.');
      return;
    }
    setSavingName(true);
    setError(null);
    try {
      const { saveProcessedModel } = await import('./modelsApi');
      const saved = await saveProcessedModel({
        companyId,
        name: nameInput,
        presentationB64: resultRefB64,
        anglesB64: resultAnglesB64,
      });
      onModelCreated();
      onConfirmModel(saved);
    } catch (err) {
      console.error('[ModelPicker] saveProcessedModel error', err);
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos guardar el modelo. Probá de nuevo.',
      );
    } finally {
      setSavingName(false);
    }
  };

  if (phase === 'processing') {
    return <ModelProcessingView />;
  }
  if (phase === 'result') {
    return (
      <ModelResultView
        imageRefB64={resultRefB64}
        imageAnglesB64={resultAnglesB64}
        name={nameInput}
        onNameChange={setNameInput}
        saving={savingName}
        error={error}
        onUtilizar={handleUtilizar}
      />
    );
  }

  return (
    <>
      {/* ── Mobile: accordion (CollapsibleSection) + botón Listo ── */}
      <div className="md:hidden flex flex-col gap-3">
        <div className="mb-1">
          <p className="text-[15.5px] font-semibold text-brand-black/90">
            Creá tu modelo
          </p>
          <p className="text-[13px] text-[#3C3C43]/60 mt-0.5">
            Definí cómo se ve para que se ajuste a tu marca.
          </p>
        </div>

        <CollapsibleSection title="Género" summary={genero || null}>
          <div className="flex flex-wrap gap-2">
            {GENERO_OPTIONS.map((g) => chip(g, genero, setGenero))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Edad" summary={edad || null}>
          <div className="flex flex-wrap gap-2">
            {EDAD_OPTIONS.map((e) => chip(e, edad, setEdad))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Tipo de cuerpo"
          summary={tipoCuerpo || null}
        >
          <div className="flex flex-wrap gap-2">
            {TIPO_CUERPO_OPTIONS.map((c) => chip(c, tipoCuerpo, setTipoCuerpo))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Tono de piel"
          summary={tonoPiel || 'Cualquiera'}
        >
          <div className="flex gap-2 flex-wrap items-center">
            <button
              type="button"
              onClick={() => setTonoPiel('')}
              className={`px-4 h-10 rounded-full border text-[14px] transition-colors ${
                tonoPiel === ''
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-brand-black/85 border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              Cualquiera
            </button>
            {SKIN_TONE_OPTIONS.map(({ label, color }) => (
              <button
                key={label}
                type="button"
                title={label}
                onClick={() => setTonoPiel(tonoPiel === label ? '' : label)}
                className={`w-9 h-9 rounded-full border transition-all ${
                  tonoPiel === label
                    ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                    : 'border-neutral-300'
                } flex items-center justify-center`}
                style={{ backgroundColor: color }}
              >
                {tonoPiel === label && (
                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                )}
              </button>
            ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Color de pelo"
          summary={colorPelo || 'Cualquiera'}
        >
          <div className="flex gap-2 flex-wrap items-center">
            <button
              type="button"
              onClick={() => setColorPelo('')}
              className={`px-4 h-10 rounded-full border text-[14px] transition-colors ${
                colorPelo === ''
                  ? 'bg-brand-primary text-white border-brand-primary'
                  : 'bg-white text-brand-black/85 border-neutral-200 hover:bg-neutral-50'
              }`}
            >
              Cualquiera
            </button>
            {HAIR_COLOR_OPTIONS.map(({ label, color }) => (
              <button
                key={label}
                type="button"
                title={label}
                onClick={() => setColorPelo(colorPelo === label ? '' : label)}
                className={`w-9 h-9 rounded-full border transition-all ${
                  colorPelo === label
                    ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                    : 'border-neutral-300'
                } flex items-center justify-center`}
                style={{ backgroundColor: color }}
              >
                {colorPelo === label && (
                  <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                )}
              </button>
            ))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection title="Largo de pelo" summary={largoPelo || null}>
          <div className="flex flex-wrap gap-2">
            {LARGO_PELO_OPTIONS.map((l) => chip(l, largoPelo, setLargoPelo))}
          </div>
        </CollapsibleSection>

        <CollapsibleSection
          title="Detalles adicionales"
          summary={descripcion ? '✓' : null}
        >
          <textarea
            value={descripcion}
            onChange={(e) => setDescripcion(e.target.value)}
            placeholder="Describilo en tus palabras…"
            rows={2}
            className="w-full resize-none px-4 py-3 rounded-2xl bg-white border border-neutral-200 text-[14px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
          />
        </CollapsibleSection>

        {error && (
          <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
            {error}
          </div>
        )}

        <button
          type="button"
          onClick={handleSubmit}
          className="mt-2 w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          Crear modelo
        </button>
        {/* Botón silencioso para satisfacer eslint con onBack */}
        <span className="sr-only">
          <button type="button" onClick={onBack}>
            back
          </button>
        </span>
      </div>

      {/* ── Desktop: layout flat estilo PruebaNuevaInterfaz ── */}
      <div className="hidden md:flex md:flex-col md:min-h-full">
        <p className="text-[15px] text-neutral-500 mb-4">
          Definí las características del modelo para que se ajuste al estilo de tu marca.
        </p>

        {/* Desktop ahora usa los mismos acordeones (CollapsibleSection) que
            mobile: cada categoría es un dropdown que despliega sus opciones.
            Los chips usan el sizing desktop (dChip, 15px). */}
        <div className="flex flex-col gap-3">
          <CollapsibleSection title="Género" summary={genero || null}>
            <div className="flex flex-wrap gap-2">
              {GENERO_OPTIONS.map((g) => dChip(g, genero, setGenero))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection title="Edad" summary={edad || null}>
            <div className="flex flex-wrap gap-2">
              {EDAD_OPTIONS.map((e) => dChip(e, edad, setEdad))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Tipo de cuerpo"
            summary={tipoCuerpo || null}
          >
            <div className="flex flex-wrap gap-2">
              {TIPO_CUERPO_OPTIONS.map((c) =>
                dChip(c, tipoCuerpo, setTipoCuerpo),
              )}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Tono de piel"
            summary={tonoPiel || 'Cualquiera'}
          >
            <div className="flex gap-2 flex-wrap items-center">
              <button
                type="button"
                onClick={() => setTonoPiel('')}
                className={`px-4 h-10 rounded-full border text-[15px] ${
                  tonoPiel === ''
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                Cualquiera
              </button>
              {SKIN_TONE_OPTIONS.map(({ label, color }) => (
                <button
                  key={label}
                  type="button"
                  title={label}
                  onClick={() => setTonoPiel(tonoPiel === label ? '' : label)}
                  className={`w-9 h-9 rounded-full border ${
                    tonoPiel === label
                      ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                      : 'border-neutral-300'
                  } flex items-center justify-center`}
                  style={{ backgroundColor: color }}
                >
                  {tonoPiel === label && (
                    <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                  )}
                </button>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Color de pelo"
            summary={colorPelo || 'Cualquiera'}
          >
            <div className="flex gap-2 flex-wrap items-center">
              <button
                type="button"
                onClick={() => setColorPelo('')}
                className={`px-4 h-10 rounded-full border text-[15px] ${
                  colorPelo === ''
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                Cualquiera
              </button>
              {HAIR_COLOR_OPTIONS.map(({ label, color }) => (
                <button
                  key={label}
                  type="button"
                  title={label}
                  onClick={() => setColorPelo(colorPelo === label ? '' : label)}
                  className={`w-9 h-9 rounded-full border ${
                    colorPelo === label
                      ? 'ring-2 ring-brand-primary ring-offset-2 border-brand-primary'
                      : 'border-neutral-300'
                  } flex items-center justify-center`}
                  style={{ backgroundColor: color }}
                >
                  {colorPelo === label && (
                    <span className="block w-3.5 h-3.5 rounded-full bg-white border border-brand-primary" />
                  )}
                </button>
              ))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Largo de pelo"
            summary={largoPelo || 'Cualquiera'}
          >
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => setLargoPelo('')}
                className={`px-4 h-10 rounded-full border text-[15px] ${
                  largoPelo === ''
                    ? 'bg-brand-primary text-white border-brand-primary'
                    : 'bg-white text-neutral-900 border-neutral-200 hover:bg-neutral-50'
                }`}
              >
                Cualquiera
              </button>
              {LARGO_PELO_OPTIONS.map((l) => dChip(l, largoPelo, setLargoPelo))}
            </div>
          </CollapsibleSection>

          <CollapsibleSection
            title="Detalles adicionales"
            summary={descripcion ? '✓' : null}
          >
            <textarea
              value={descripcion}
              onChange={(e) => setDescripcion(e.target.value)}
              placeholder="Describilo en tus palabras…"
              rows={3}
              className="w-full resize-none px-4 py-3 rounded-lg bg-white border border-neutral-200 text-[15px] text-neutral-900 placeholder:text-neutral-400 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
            />
          </CollapsibleSection>

          {error && (
            <div className="rounded-lg bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
              {error}
            </div>
          )}
        </div>

        {/* Footer: Cancelar + Crear modelo — sticky al bottom del scroll del
            wrapper padre. -mx-5 px-5 hace que el bg-white cubra hasta
            los bordes del sheet (el wrapper tiene px-5). El padre del
            wrapper desktop tiene md:pb-0 para que el footer toque el
            fondo del sheet. */}
        <div className="sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white flex justify-end gap-3 border-t border-black/[0.06]">
          <button
            type="button"
            onClick={onBack}
            className="text-[15px] text-[#3C3C43]/60 hover:text-brand-black/80 px-3 py-2 transition-colors"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            className="text-[15px] font-semibold text-white bg-brand-primary hover:bg-brand-primary-hover rounded-md px-4 py-2 shadow-sm transition-colors"
          >
            Crear modelo
          </button>
        </div>
      </div>
    </>
  );
};

// ─── Shared: ModelProcessingView ───────────────────────────────────────
// Spinner + mensaje rotativo mientras el backend procesa/crea el modelo.
// Reutilizado por UploadFlow (subir fotos) y CreateView (crear desde cero).

const MODEL_LOADING_MESSAGES_DEFAULT = [
  'Analizando las características…',
  'Diseñando al modelo…',
  'Generando vista previa…',
  'Casi listo…',
];

const ModelProcessingView: React.FC<{ messages?: string[] }> = ({
  messages = MODEL_LOADING_MESSAGES_DEFAULT,
}) => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    setIdx(0);
    const t = setInterval(() => {
      setIdx((i) => Math.min(i + 1, messages.length - 1));
    }, 4500);
    return () => clearInterval(t);
  }, [messages.length]);
  return (
    <div className="flex flex-col items-center justify-center text-center gap-5 min-h-[60dvh]">
      <div className="relative w-16 h-16">
        <div
          className="absolute inset-0 rounded-full border-4 border-brand-primary/15 border-t-brand-primary animate-spin"
          style={{ animationDuration: '1s' }}
        />
      </div>
      <div>
        <p className="text-[16px] font-semibold text-brand-black/90">
          {messages[idx]}
        </p>
        <p className="text-[13px] text-[#3C3C43]/55 mt-1.5 max-w-sm">
          Esto puede tardar un minuto
        </p>
      </div>
    </div>
  );
};

// ─── Shared: ModelResultView ───────────────────────────────────────────
// Preview del modelo generado (presentación + ángulos en carrusel) + input
// de nombre + footer Utilizar/Aceptar. El backend ya devolvió los b64; la
// persistencia ocurre al tocar Utilizar (vía el `onUtilizar` del padre).

const ModelResultView: React.FC<{
  imageRefB64: string | null;
  imageAnglesB64: string | null;
  name: string;
  onNameChange: (v: string) => void;
  saving: boolean;
  error: string | null;
  onUtilizar: () => void;
}> = ({
  imageRefB64,
  imageAnglesB64,
  name,
  onNameChange,
  saving,
  error,
  onUtilizar,
}) => {
  const [slide, setSlide] = useState<0 | 1>(0);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const onScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const i = Math.round(el.scrollLeft / el.clientWidth);
    setSlide(i === 0 ? 0 : 1);
  };
  const scrollTo = (i: 0 | 1) => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollTo({ left: el.clientWidth * i, behavior: 'smooth' });
  };
  return (
    <div className="flex flex-col gap-5 md:min-h-full">
      <div className="md:hidden">
        <p className="text-[16px] font-semibold text-brand-black/90">
          Tu modelo está listo
        </p>
        <p className="text-[13.5px] text-[#3C3C43]/60 mt-1 leading-relaxed">
          Ponele un nombre (opcional) y tocá Utilizar para guardarlo y
          usarlo en tu generación.
        </p>
      </div>

      {(imageRefB64 || imageAnglesB64) && (
        <div className="flex flex-col gap-3">
          {/* El alto del carrusel NO es fijo: cada slide ocupa el alto
              natural de su contenido y, por el stretch del flex, ambos
              slides quedan tan altos como el más alto (slide 2 = banner +
              imagen de ángulos). Así no hay scroll vertical interno. La
              presentación (slide 1) se centra con un tope de alto para no
              dominar. */}
          <HoverScrollCarousel
            innerRef={scrollRef}
            onScroll={onScroll}
            arrowInset={4}
            className="flex items-stretch overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide scroll-smooth"
            style={{
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              WebkitOverflowScrolling: 'touch',
            }}
          >
            {imageRefB64 && (
              <div className="snap-center shrink-0 w-full flex items-center justify-center">
                <img
                  src={`data:image/png;base64,${imageRefB64}`}
                  alt="Modelo generado"
                  className="max-h-[48dvh] w-auto max-w-full h-auto object-contain rounded-2xl bg-neutral-100"
                />
              </div>
            )}
            {imageAnglesB64 && (
              <div className="snap-center shrink-0 w-full flex flex-col justify-center gap-3">
                <div className="rounded-xl bg-brand-primary/[0.06] border border-brand-primary/15 px-3.5 py-3">
                  <p className="text-[13.5px] text-[#3C3C43]/75 leading-relaxed">
                    Analizamos a tu modelo desde varios ángulos para que se
                    mantenga consistente en las imágenes que genere.
                  </p>
                </div>
                <img
                  src={`data:image/png;base64,${imageAnglesB64}`}
                  alt="Ángulos del modelo"
                  className="w-full h-auto rounded-xl"
                />
              </div>
            )}
          </HoverScrollCarousel>
          {imageRefB64 && imageAnglesB64 && (
            <div className="flex items-center justify-center gap-1.5">
              {[0, 1].map((i) => (
                <button
                  key={i}
                  type="button"
                  onClick={() => scrollTo(i as 0 | 1)}
                  aria-label={`Ver ${i === 0 ? 'presentación' : 'ángulos'}`}
                  className={`h-1.5 rounded-full transition-all ${
                    slide === i
                      ? 'w-5 bg-brand-primary'
                      : 'w-1.5 bg-neutral-300 hover:bg-neutral-400'
                  }`}
                />
              ))}
            </div>
          )}
        </div>
      )}

      <div>
        <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
          Nombre del modelo
        </label>
        <input
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          placeholder="Opcional"
          disabled={saving}
          className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
        />
      </div>

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* Mobile: pill full-width "Utilizar". */}
      <button
        type="button"
        onClick={onUtilizar}
        disabled={saving}
        className="md:hidden w-full inline-flex items-center justify-center py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
      >
        {saving ? 'Guardando…' : 'Utilizar'}
      </button>
      {/* Desktop: footer al bottom (mt-auto + sticky). */}
      <div className="hidden md:flex sticky bottom-0 mt-auto -mx-5 px-5 pt-3 pb-3 bg-white justify-end gap-3 border-t border-black/[0.06]">
        <button
          type="button"
          onClick={onUtilizar}
          disabled={saving}
          className={`text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors ${
            saving
              ? 'text-neutral-400 bg-neutral-100 border border-neutral-200 cursor-not-allowed'
              : 'text-white bg-brand-primary hover:bg-brand-primary-hover'
          }`}
        >
          {saving ? 'Guardando…' : 'Aceptar'}
        </button>
      </div>
    </div>
  );
};

// ─── Sub-componente: UploadFlow ────────────────────────────────────────
// "Cargá tu propio modelo": una sola pantalla, estilo "subir producto" del
// estudio clásico. El user sube UNA imagen + nombre opcional, y al confirmar:
//   1. La imagen CRUDA se guarda en storage `modelos/<company>/full/` + se
//      inserta la fila en `modelos` (esa imagen queda como thumbnail).
//   2. Se dispara el webhook `procesar_modelo_propio` fire-and-forget (con el
//      id del modelo) para que el backend lo procese en segundo plano.
//   3. Se devuelve el modelo recién guardado al instante (sin pantallas de
//      processing/preview — el modelo queda usable ya mismo).
// (Ver `uploadOwnModelDirect` en modelsApi.)

interface UploadFlowProps {
  /** id del user — para el webhook procesar_modelo_propio. */
  userId: string | null;
  /** id_company del user — necesario para guardar el modelo. */
  companyId: string | null;
  /** Llamado cuando el modelo queda guardado — pasa el modelo creado. */
  onUtilizar: (model: ModelV2) => void;
  /** Llamado tras guardar el modelo en DB. El padre lo usa para refrescar
   *  su lista de userModels. */
  onModelCreated: () => void;
}

export const UploadFlow: React.FC<UploadFlowProps> = ({
  userId,
  companyId,
  onUtilizar,
  onModelCreated,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [nameInput, setNameInput] = useState<string>('');
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<boolean>(false);
  const [convertingHeic, setConvertingHeic] = useState<boolean>(false);

  const fileRef = useRef<HTMLInputElement | null>(null);

  // Preview local del archivo elegido (cleanup para no leakear).
  const [preview, setPreview] = useState<string | null>(null);
  useEffect(() => {
    if (!file) {
      setPreview(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const handlePick = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const picked = e.target.files?.[0] ?? null;
    e.target.value = '';
    if (!picked) return;
    setError(null);
    // HEIC/HEIF/AVIF (iPhone) → JPEG, así se ve el preview y se sube un formato
    // soportado. Usa el mismo helper que el resto de los uploads de V2 (cubre
    // AVIF además de HEIC, que es lo que exporta iOS reciente por default).
    if (isHeicFile(picked) || isAvifFile(picked)) {
      try {
        setConvertingHeic(true);
        setFile(await normalizeImageForUpload(picked));
      } catch {
        setError('No pudimos procesar esa imagen. Probá con otra (JPG o PNG).');
      } finally {
        setConvertingHeic(false);
      }
    } else {
      setFile(picked);
    }
  };

  const handleConfirm = async () => {
    if (!userId || !companyId) {
      setError('No pudimos identificar al usuario. Probá recargar.');
      return;
    }
    if (!file) return;
    setSaving(true);
    setError(null);
    try {
      const { uploadOwnModelDirect } = await import('./modelsApi');
      const saved = await uploadOwnModelDirect({
        companyId,
        userId,
        name: nameInput,
        file,
      });
      onModelCreated();
      onUtilizar(saved);
    } catch (err) {
      console.error('[ModelPicker] uploadOwnModelDirect error', err);
      setError(
        err instanceof Error
          ? err.message
          : 'No pudimos guardar el modelo. Probá de nuevo.',
      );
      setSaving(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 h-full md:h-auto md:min-h-full">
      {/* Nombre (opcional). */}
      <div>
        <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
          Nombre del modelo
        </label>
        <input
          type="text"
          value={nameInput}
          onChange={(e) => setNameInput(e.target.value)}
          placeholder="Ej: María, Juan, etc."
          disabled={saving}
          className="w-full h-11 px-4 rounded-xl bg-white border border-neutral-200 text-[14.5px] text-brand-black/90 placeholder:text-[#3C3C43]/40 focus:outline-none focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary disabled:opacity-50 transition-colors"
        />
      </div>

      {/* Foto del rostro — container con la foto de ejemplo + aclaraciones al
          lado (estilo "Añadir modelo real" del estudio clásico). */}
      <div>
        <label className="block text-[12.5px] uppercase tracking-wide font-medium text-[#3C3C43]/55 mb-1.5">
          Foto del rostro
        </label>
        {!file ? (
          <div className="rounded-2xl border border-neutral-200 bg-white p-3.5">
            <div className="flex gap-3">
              <div className="w-24 h-24 shrink-0 rounded-xl border border-neutral-200 bg-white overflow-hidden relative">
                <img
                  src="/images/cara_modelo_ejemplo.png"
                  alt="Ejemplo"
                  className="w-full h-full object-cover"
                />
                <span className="absolute bottom-1 right-1 inline-flex items-center justify-center w-6 h-6 rounded-full bg-success text-white shadow-sm">
                  <Check className="w-3.5 h-3.5" strokeWidth={3} />
                </span>
              </div>
              <ul className="text-[13px] text-[#3C3C43]/65 list-disc pl-4 space-y-1 leading-snug">
                <li>
                  Subí una foto en donde se vea claramente el rostro de la
                  persona.
                </li>
                <li>
                  Evitá fotos con lentes de sol, máscaras o elementos que tapen
                  la cara.
                </li>
                <li>Usá una foto con buena iluminación y fondo simple.</li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="rounded-2xl border border-neutral-200 bg-white p-3">
            <div className="flex items-center gap-3">
              <div className="w-20 h-20 shrink-0 rounded-xl overflow-hidden bg-neutral-100 ring-1 ring-black/[0.05] relative">
                {preview && (
                  <img
                    src={preview}
                    alt="Foto del rostro"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] text-brand-black/80 truncate">
                  {file.name}
                </p>
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  disabled={saving}
                  className="text-[13px] text-red-600 hover:opacity-70 disabled:opacity-40 mt-1"
                >
                  Eliminar
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Desktop: botón "Subir imagen del modelo" debajo del container
            (donde estaba). Solo desktop y solo mientras no haya imagen. En
            mobile el botón vive bottom-aligned en el footer. */}
        {!file && (
          <button
            type="button"
            onClick={() => fileRef.current?.click()}
            disabled={convertingHeic}
            className="hidden md:flex mt-3 w-full items-center justify-center gap-2.5 py-3.5 px-6 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 text-white text-[15.5px] font-semibold shadow-sm transition-colors"
          >
            <Upload className="w-5 h-5" />
            {convertingHeic ? 'Procesando…' : 'Subir imagen del modelo'}
          </button>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        accept="image/*,.heic,.heif,.avif"
        className="hidden"
        onChange={handlePick}
      />

      {error && (
        <div className="rounded-xl bg-red-50 border border-red-200 px-3 py-2.5 text-[13px] text-red-700">
          {error}
        </div>
      )}

      {/* Footer. MOBILE: un botón bottom-aligned que morfea (sin imagen =
          "Subir imagen del modelo" abre el picker; con imagen = "Guardar
          modelo"). DESKTOP: acá va SOLO "Guardar modelo", y solo cuando hay
          imagen — el "Subir imagen del modelo" de desktop vive arriba, debajo
          del container. */}
      <div className="mt-auto">
        {/* Mobile: pill full-width bottom-aligned. */}
        <button
          type="button"
          onClick={file ? handleConfirm : () => fileRef.current?.click()}
          disabled={file ? saving : convertingHeic}
          className="md:hidden w-full inline-flex items-center justify-center gap-2 py-3 rounded-2xl bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed text-white text-[14.5px] font-semibold shadow-sm transition-colors"
        >
          {!file && <Upload className="w-4 h-4" />}
          {file
            ? saving
              ? 'Guardando…'
              : 'Guardar modelo'
            : convertingHeic
              ? 'Procesando…'
              : 'Subir imagen del modelo'}
        </button>
        {/* Desktop: "Guardar modelo" en el footer, solo cuando hay imagen. */}
        {file && (
          <div className="hidden md:flex sticky bottom-0 -mx-5 px-5 pt-3 pb-3 bg-white justify-end gap-3 border-t border-black/[0.06]">
            <button
              type="button"
              onClick={handleConfirm}
              disabled={saving}
              className="text-[15px] font-semibold rounded-md px-4 py-2 shadow-sm transition-colors text-white bg-brand-primary hover:bg-brand-primary-hover disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {saving ? 'Guardando…' : 'Guardar modelo'}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── ModelPickerInline (desktop) ───────────────────────────────────────
// Embedded en el ConfigurePanel desktop. Renderiza directamente la vista
// activa, sin sheet ni overlay. Cuando el user toca "Crea / Cargá", el
// contenido se reemplaza con la sub-vista; un botón Volver lo trae de
// vuelta al list view.

export type ModelPickerView = 'list' | 'create' | 'upload' | 'multi';

interface ModelPickerInlineProps {
  selection: ModelSelection | null;
  onChange: (sel: ModelSelection) => void;
  /**
   * Vista actual del picker. Controlada por el padre así puede esconder
   * el resto del ConfigurePanel cuando el user está en create/upload
   * (queremos que ese sub-flow ocupe todo el panel del costado).
   */
  view: ModelPickerView;
  onViewChange: (v: ModelPickerView) => void;
  /** Modelos reales del user (globales + privados). */
  userModels: ModelV2[];
  /** id_auth del user — necesario para el webhook procesar_modelo_propio. */
  userId: string | null;
  /** id_company del user — necesario para guardar el modelo al Utilizar. */
  companyId: string | null;
  /** Callback post-upload — el padre refresca su lista de userModels. */
  onModelCreated: () => void;
  /** Si true, oculta el label "Modelo" interno (lo provee el caller). */
  hideLabel?: boolean;
  /** Opcional: si está provisto, los botones "Creá tu modelo" / "Cargá
   *  tu modelo" llaman a este callback en vez de cambiar el view inline.
   *  Lo usa design-modelo en desktop para abrir el sheet como side panel. */
  onOpenSheet?: (initialView: ModelPickerView) => void;
  /** Opcional: variante del anterior para el sub-flow de multi. Cuando
   *  el user está editando un slot del multi y toca Crea/Carga, el
   *  inline llama esto en vez de `onOpenSheet` — el padre abre el sheet
   *  con la sub-vista pedida Y el slot pre-seteado, así el sheet aplica
   *  la selección al slot correcto. */
  onOpenSheetForMultiSlot?: (
    slotIdx: number,
    initialView: 'create' | 'upload',
  ) => void;
  // ── Multi-modelo (opcional). Si `multiEnabled` está true, el picker
  // arranca en la view 'multi' con los containers. El padre persiste
  // selectionsExtra y el flag para que el estado sobreviva al cierre
  // del picker. Si no se pasan los handlers, el CTA "Utilizar varios
  // modelos" no se renderiza.
  /** Selecciones extra (slots adicionales al principal). Tope max = 3
   *  para totalizar 4 modelos contando el slot principal. */
  selectionsExtra?: (ModelSelection | null)[];
  /** Setter que reemplaza el array completo. */
  onChangeExtras?: (next: (ModelSelection | null)[]) => void;
  /** Flag de multi activo. */
  multiEnabled?: boolean;
  /** Setter del flag. */
  onMultiEnabledChange?: (v: boolean) => void;
  /** Productos seleccionados — se muestran en cada slot bajo "Utilizando". */
  multiProducts?: MultiPickerProduct[];
  /** Productos específicos por slot. Si no se pasa, todos los slots
   *  muestran `multiProducts`. */
  multiProductsPerSlot?: Record<number, MultiPickerProduct[]>;
  /** Tap en "Utilizando"/miniaturas de un slot — abre el picker de
   *  productos para ese slot. */
  onTapSlotProducts?: (slotIdx: number) => void;
  /** Si true, oculta los botones para agregar/quitar slots — la
   *  cantidad de modelos queda fija (definida por el flow padre, p.ej.
   *  una pose de E-commerce de 2+ personas). */
  lockSlotCount?: boolean;
}

// Máximo de modelos en multi-modelo. Tope = 3 porque NO existen poses de 4
// personas (la tabla `poses` tiene 1/2/3); con 4 el picker de poses quedaba
// vacío. Fuente única: se pasa como `maxSlots` a los MultiView.
const MAX_MULTI_SLOTS = 3;

export const ModelPickerInline: React.FC<ModelPickerInlineProps> = ({
  selection,
  onChange,
  view,
  onViewChange,
  userModels,
  userId,
  companyId,
  onModelCreated,
  hideLabel = false,
  onOpenSheet,
  onOpenSheetForMultiSlot,
  selectionsExtra = [],
  onChangeExtras,
  onMultiEnabledChange,
  multiProducts = [],
  multiProductsPerSlot,
  onTapSlotProducts,
  lockSlotCount = false,
}) => {
  // Sub-state: cuando estamos en view='multi' y el user tapea un slot,
  // entramos a la sub-list view para elegir un modelo. editingMultiIdx
  // indica qué slot está siendo editado (0 = principal, 1..3 = extras).
  // null = mostrar los containers.
  const [editingMultiIdx, setEditingMultiIdx] = useState<number | null>(null);
  const isMultiEditing = view === 'multi' && editingMultiIdx !== null;
  const showMultiContainers = view === 'multi' && editingMultiIdx === null;

  // Array combinado para pasar al MultiView. Para los slots tipo
  // 'preset', refrescamos la `imageUrl` desde `userModels` (signed URL
  // recién emitida del fetch) — la URL almacenada en la selección
  // puede haber sido firmada hace rato (incluso de otra sesión vía
  // localStorage) y la imagen mostraría rota.
  const refreshSlotUrl = (s: ModelSelection | null): ModelSelection | null => {
    if (!s || s.mode !== 'preset') return s;
    const fresh = userModels.find((m) => String(m.id) === s.id);
    if (!fresh || !fresh.thumbnailUrl) return s;
    return { ...s, imageUrl: fresh.thumbnailUrl };
  };
  const allSlots: (ModelSelection | null)[] = [
    refreshSlotUrl(selection),
    ...selectionsExtra.map(refreshSlotUrl),
  ];

  const handleActivateMulti = () => {
    if (!onMultiEnabledChange) return;
    onMultiEnabledChange(true);
    // Slot principal hereda la selección actual; añadimos un slot extra
    // en Automático (la IA elige) para nacer en modo multi con 2 containers.
    onChangeExtras?.([{ mode: 'auto' }]);
    onViewChange('multi');
    setEditingMultiIdx(null);
  };

  const handleAddSlot = () => {
    if (!onChangeExtras) return;
    if (allSlots.length >= MAX_MULTI_SLOTS) return;
    // El slot nuevo nace en Automático (la IA elige el modelo), no vacío.
    onChangeExtras([...selectionsExtra, { mode: 'auto' }]);
  };

  const handleRemoveSlot = (idx: number) => {
    if (!onMultiEnabledChange || !onChangeExtras) return;
    // Quitamos el slot idx del array combinado y reasignamos:
    //   - resultado[0] → slot principal
    //   - resultado[1..] → selectionsExtra
    const next = allSlots.filter((_, i) => i !== idx);
    if (next.length <= 1) {
      // Volvemos a single mode con el survivor (o null = auto).
      onChange(next[0] ?? { mode: 'auto' });
      onChangeExtras([]);
      onMultiEnabledChange(false);
      onViewChange('list');
      setEditingMultiIdx(null);
      return;
    }
    onChange(next[0] ?? { mode: 'auto' });
    onChangeExtras(next.slice(1));
  };

  const handleTapSlot = (idx: number) => {
    setEditingMultiIdx(idx);
  };

  const handleSelectForEditingSlot = (sel: ModelSelection) => {
    if (editingMultiIdx === null) return;
    if (editingMultiIdx === 0) {
      onChange(sel);
    } else {
      const extraIdx = editingMultiIdx - 1;
      const nextExtras = selectionsExtra.map((s, i) =>
        i === extraIdx ? sel : s,
      );
      onChangeExtras?.(nextExtras);
    }
    setEditingMultiIdx(null);
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Header con label + (si aplica) botón Volver / Atrás */}
      <div
        className={`flex items-center ${hideLabel ? 'justify-end' : 'justify-between'}`}
      >
        {!hideLabel && (
          <p className="text-[11px] uppercase tracking-wide font-medium text-[#3C3C43]/55">
            Modelo
          </p>
        )}
        {isMultiEditing ? (
          <button
            type="button"
            onClick={() => setEditingMultiIdx(null)}
            className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
          >
            <ChevronLeft className="w-3.5 h-3.5" />
            Atrás
          </button>
        ) : (
          view !== 'list' &&
          view !== 'multi' && (
            <button
              type="button"
              onClick={() => onViewChange('list')}
              className="inline-flex items-center gap-0.5 text-brand-primary text-[12.5px] font-medium hover:opacity-75 transition-opacity"
            >
              <ChevronLeft className="w-3.5 h-3.5" />
              Volver
            </button>
          )
        )}
      </div>

      {/* Wrapper de animación: la `key` dispara `animate-mode-swap`
          cada vez que cambia el view o el editing slot, dando una
          transición sutil (fade + 4px translate) entre estados. */}
      <div
        key={`${view}-${editingMultiIdx ?? 'none'}`}
        className="animate-mode-swap"
      >
      {view === 'list' && (
        <ListView
          selection={selection}
          models={userModels}
          variant="inline"
          onSelectAuto={() => onChange({ mode: 'auto' })}
          onSelectPreset={(m) =>
            onChange({
              mode: 'preset',
              id: String(m.id),
              name: m.nombre,
              imageUrl: m.thumbnailUrl,
            })
          }
          onTapCreate={() =>
            onOpenSheet ? onOpenSheet('create') : onViewChange('create')
          }
          onTapUpload={() =>
            onOpenSheet ? onOpenSheet('upload') : onViewChange('upload')
          }
          onTapMulti={onMultiEnabledChange ? handleActivateMulti : undefined}
        />
      )}
      {showMultiContainers && (
        <MultiView
          slots={allSlots}
          products={multiProducts}
          productsPerSlot={multiProductsPerSlot}
          onTapSlot={handleTapSlot}
          onRemoveSlot={handleRemoveSlot}
          onTapSlotProducts={onTapSlotProducts}
          onAddSlot={onChangeExtras ? handleAddSlot : undefined}
          maxSlots={MAX_MULTI_SLOTS}
          lockSlotCount={lockSlotCount}
        />
      )}
      {isMultiEditing && (
        <ListView
          selection={
            editingMultiIdx !== null ? allSlots[editingMultiIdx] : null
          }
          models={userModels}
          variant="inline"
          onSelectAuto={() =>
            handleSelectForEditingSlot({ mode: 'auto' })
          }
          onSelectPreset={(m) =>
            handleSelectForEditingSlot({
              mode: 'preset',
              id: String(m.id),
              name: m.nombre,
              imageUrl: m.thumbnailUrl,
            })
          }
          // En sub-flow del multi delegamos al sheet pre-configurado
          // con el slot a editar — así create/upload se muestran en el
          // side panel desktop como en single mode, manteniendo el
          // contexto del slot. Limpiamos el editingMultiIdx local para
          // que al cerrar el sheet el inline vuelva al MultiView (no a
          // sub-list).
          onTapCreate={() => {
            const idx = editingMultiIdx;
            if (idx === null) return;
            setEditingMultiIdx(null);
            if (onOpenSheetForMultiSlot) {
              onOpenSheetForMultiSlot(idx, 'create');
            } else if (onOpenSheet) {
              onOpenSheet('create');
            } else {
              onViewChange('create');
            }
          }}
          onTapUpload={() => {
            const idx = editingMultiIdx;
            if (idx === null) return;
            setEditingMultiIdx(null);
            if (onOpenSheetForMultiSlot) {
              onOpenSheetForMultiSlot(idx, 'upload');
            } else if (onOpenSheet) {
              onOpenSheet('upload');
            } else {
              onViewChange('upload');
            }
          }}
        />
      )}
      {view === 'create' && (
        <CreateView
          initial={selection?.mode === 'custom' ? selection.fields : undefined}
          userId={userId}
          companyId={companyId}
          onModelCreated={onModelCreated}
          onBack={() => onViewChange('list')}
          onConfirmModel={(model) => {
            onChange({
              mode: 'preset',
              id: String(model.id),
              name: model.nombre,
              imageUrl: model.thumbnailUrl,
            });
            onViewChange('list');
          }}
        />
      )}
      {view === 'upload' && (
        <UploadFlow
          userId={userId}
          companyId={companyId}
          onModelCreated={onModelCreated}
          onUtilizar={(model) => {
            onChange({
              mode: 'preset',
              id: String(model.id),
              name: model.nombre,
              imageUrl: model.thumbnailUrl,
            });
            onViewChange('list');
          }}
        />
      )}
      </div>
    </div>
  );
};

// ─── ModelPickerSheet (mobile) ─────────────────────────────────────────
// Bottom sheet con drag handle + header iOS. Navega entre las 3 vistas
// con un botón Volver en el header cuando no estamos en 'list'.

interface ModelPickerSheetProps {
  open: boolean;
  selection: ModelSelection | null;
  onChange: (sel: ModelSelection) => void;
  onClose: () => void;
  userModels: ModelV2[];
  userId: string | null;
  companyId: string | null;
  onModelCreated: () => void;
  /** Vista inicial al abrir — útil para entrar directo a 'create' o
   *  'upload' desde el inline picker en desktop. Default 'list'. */
  initialView?: ModelPickerView;
  // ── Multi-modelo (opcional). El sheet maneja su propio
  // editingMultiIdx internamente, pero el flag `multiEnabled` y las
  // selecciones extra son persistidas por el padre. Si los handlers
  // no se pasan, el CTA "Utilizar varios modelos" no se renderiza.
  /** Slots extra (1..3) que se suman al slot principal (selection). */
  selectionsExtra?: (ModelSelection | null)[];
  /** Setter del array completo de extras. */
  onChangeExtras?: (next: (ModelSelection | null)[]) => void;
  multiEnabled?: boolean;
  onMultiEnabledChange?: (v: boolean) => void;
  multiProducts?: MultiPickerProduct[];
  /** Productos específicos por slot. Si no se pasa, todos los slots
   *  muestran `multiProducts`. */
  multiProductsPerSlot?: Record<number, MultiPickerProduct[]>;
  /** Tap en "Utilizando"/miniaturas de un slot — el padre abre el
   *  product picker para ese slot. */
  onTapSlotProducts?: (slotIdx: number) => void;
  /** Slot que el sheet va a editar al abrirse — usado cuando el inline
   *  delega create/upload al sheet. Si está seteado, el sheet arranca
   *  con `editingMultiIdx` apuntando a este slot y `view = initialView`
   *  (normalmente 'create' o 'upload'), para que aplique al slot
   *  correcto sin que el user tenga que re-elegirlo. */
  initialEditingMultiIdx?: number | null;
  /** Si true, oculta los botones para agregar/quitar slots — la
   *  cantidad de modelos queda fija (definida por el flow padre, p.ej.
   *  una pose de E-commerce de 2+ personas). */
  lockSlotCount?: boolean;
  /** Si true, no se muestra el tile "Automático" en la lista. */
  hideAuto?: boolean;
}

export const ModelPickerSheet: React.FC<ModelPickerSheetProps> = ({
  open,
  selection,
  onChange,
  onClose,
  userModels,
  userId,
  companyId,
  onModelCreated,
  initialView = 'list',
  selectionsExtra = [],
  onChangeExtras,
  multiEnabled = false,
  onMultiEnabledChange,
  multiProducts = [],
  multiProductsPerSlot,
  onTapSlotProducts,
  initialEditingMultiIdx = null,
  lockSlotCount = false,
  hideAuto = false,
}) => {
  const [view, setView] = useState<ModelPickerView>(initialView);
  // Sub-state: cuando view='multi' y el user tapea uno de los slots,
  // entramos a la sub-list view para elegir un modelo. null = mostrar
  // los containers normales del multi.
  const [editingMultiIdx, setEditingMultiIdx] = useState<number | null>(null);
  // `useExitTransition` mantiene el sheet montado durante la animación
  // de salida cuando `open` pasa a false (al seleccionar un modelo, tocar
  // Cancelar o el overlay) — así el deslizamiento inverso de cierre se
  // ejecuta antes de desmontar el componente.
  const { mounted, animated } = useExitTransition(open, 320);

  // Sincronizar sub-view al abrir el sheet (`view` y `editingMultiIdx`).
  // El cleanup interno (reset a 'list' después del cierre) lo hace este
  // mismo effect cuando `open` pasa a false, con un delay para no ver el
  // wipe mientras corre la animación de salida.
  useEffect(() => {
    if (open) {
      // Precedencia al abrir:
      //  1. Si el padre nos pasó `initialEditingMultiIdx`, el inline
      //     delegó un sub-flow create/upload de un slot multi —
      //     respetamos el initialView y pre-seteamos el slot a editar.
      //  2. Si multi está activo (sin slot pre-set), mostramos los
      //     containers.
      //  3. Sino, abrimos en initialView (default 'list').
      if (initialEditingMultiIdx !== null) {
        setView(initialView);
        setEditingMultiIdx(initialEditingMultiIdx);
      } else if (multiEnabled) {
        setView('multi');
        setEditingMultiIdx(null);
      } else {
        setView(initialView);
        setEditingMultiIdx(null);
      }
      return;
    }
    // Reset interno con delay para no ver el wipe durante la animación.
    const t = setTimeout(() => {
      setView('list');
      setEditingMultiIdx(null);
    }, 320);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  if (!mounted) return null;

  const isMultiEditing = view === 'multi' && editingMultiIdx !== null;
  const showMultiContainers = view === 'multi' && editingMultiIdx === null;

  const titleByView: Record<ModelPickerView, string> = {
    list: 'Modelo',
    create: 'Modelo personalizado',
    upload: 'Cargá tu modelo',
    multi: 'Modelos',
  };
  const headerTitle = isMultiEditing ? 'Elegí un modelo' : titleByView[view];

  // Array combinado para pasar al MultiView (slot principal + extras).
  // Refrescamos `imageUrl` de los slots 'preset' con la signed URL fresca
  // de `userModels` para evitar mostrar la stored stale (que puede venir
  // de localStorage / persist y estar caducada).
  const refreshSlotUrl = (s: ModelSelection | null): ModelSelection | null => {
    if (!s || s.mode !== 'preset') return s;
    const fresh = userModels.find((m) => String(m.id) === s.id);
    if (!fresh || !fresh.thumbnailUrl) return s;
    return { ...s, imageUrl: fresh.thumbnailUrl };
  };
  const allSlots: (ModelSelection | null)[] = [
    refreshSlotUrl(selection),
    ...selectionsExtra.map(refreshSlotUrl),
  ];

  const handleActivateMulti = () => {
    if (!onMultiEnabledChange) return;
    onMultiEnabledChange(true);
    // Slot principal hereda la selección actual; añadimos un slot extra
    // en Automático (la IA elige) para arrancar el modo multi con 2 containers.
    onChangeExtras?.([{ mode: 'auto' }]);
    setView('multi');
    setEditingMultiIdx(null);
  };

  const handleAddSlot = () => {
    if (!onChangeExtras) return;
    if (allSlots.length >= MAX_MULTI_SLOTS) return;
    // El slot nuevo nace en Automático (la IA elige el modelo), no vacío.
    onChangeExtras([...selectionsExtra, { mode: 'auto' }]);
  };

  const handleRemoveSlot = (idx: number) => {
    if (!onMultiEnabledChange || !onChangeExtras) return;
    const next = allSlots.filter((_, i) => i !== idx);
    if (next.length <= 1) {
      onChange(next[0] ?? { mode: 'auto' });
      onChangeExtras([]);
      onMultiEnabledChange(false);
      setView('list');
      setEditingMultiIdx(null);
      return;
    }
    onChange(next[0] ?? { mode: 'auto' });
    onChangeExtras(next.slice(1));
  };

  const handleSelectForEditingSlot = (sel: ModelSelection) => {
    if (editingMultiIdx === null) return;
    if (editingMultiIdx === 0) {
      onChange(sel);
    } else {
      const extraIdx = editingMultiIdx - 1;
      const nextExtras = selectionsExtra.map((s, i) =>
        i === extraIdx ? sel : s,
      );
      onChangeExtras?.(nextExtras);
    }
    setEditingMultiIdx(null);
  };

  // ¿Estamos en sub-flow de multi entrando a create/upload desde un
  // slot? `editingMultiIdx` se mantiene seteado aunque view cambió de
  // 'multi' a 'create'/'upload', porque vinimos del sub-list.
  const isMultiSubFlow =
    (view === 'create' || view === 'upload') && editingMultiIdx !== null;

  // Botón izquierdo del header: Cancelar (list root) · Atrás (sub-list
  // bajo multi, o create/upload bajo multi) · Volver (create/upload
  // desde single mode).
  const leftButton = (() => {
    if (isMultiEditing) {
      return (
        <button
          type="button"
          onClick={() => setEditingMultiIdx(null)}
          className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity"
        >
          <ArrowLeft className="w-4 h-4" />
          Atrás
        </button>
      );
    }
    if (isMultiSubFlow) {
      return (
        <button
          type="button"
          onClick={() => setView('multi')}
          className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity"
        >
          <ArrowLeft className="w-4 h-4" />
          Atrás
        </button>
      );
    }
    if (view === 'list' || view === 'multi') {
      return (
        <button
          type="button"
          onClick={onClose}
          className="text-[15px] text-brand-primary active:opacity-50 transition-opacity"
        >
          Cancelar
        </button>
      );
    }
    return (
      <button
        type="button"
        onClick={() => setView('list')}
        className="inline-flex items-center gap-0.5 text-brand-primary text-[15px] active:opacity-50 transition-opacity"
      >
        <ArrowLeft className="w-4 h-4" />
        Volver
      </button>
    );
  })();

  // Para la sub-list view (editando un slot multi), los handlers de
  // selección aplican al slot correspondiente y vuelven a multi sin
  // cerrar el sheet.
  const subListHandlers = {
    onSelectAuto: () => handleSelectForEditingSlot({ mode: 'auto' }),
    onSelectPreset: (m: ModelV2) =>
      handleSelectForEditingSlot({
        mode: 'preset',
        id: String(m.id),
        name: m.nombre,
        imageUrl: m.thumbnailUrl,
      }),
    onTapCreate: () => setView('create'),
    onTapUpload: () => setView('upload'),
  };

  return ReactDOM.createPortal(
    <>
      <div
        className={`fixed inset-0 z-[210] bg-black/40 transition-opacity duration-300 ${
          animated ? 'opacity-100' : 'opacity-0'
        }`}
        onClick={onClose}
      />
      <div
        className={`fixed z-[211] bg-white flex flex-col shadow-2xl transition-transform duration-300 ease-out
          inset-x-0 bottom-0 rounded-t-[13px] h-[calc(100dvh-1rem-env(safe-area-inset-top,0px))]
          md:inset-x-auto md:left-auto md:top-4 md:right-4 md:bottom-4 md:h-auto md:w-[630px] md:rounded-[13px]
          ${
            animated
              ? 'translate-y-0 md:translate-x-0'
              : 'translate-y-full md:translate-y-0 md:translate-x-[calc(100%+32px)]'
          }`}
        style={{
          // En mobile el sheet es bottom-anclado y su altura ya excluye
          // el safe area top (en el className arriba), así no se extiende
          // detrás del notch / Dynamic Island dejando una zona blanca
          // inútil arriba — mismo patrón que usa PruebaNuevaInterfaz.
          paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 16px)',
        }}
      >
        {/* Drag handle — solo mobile. */}
        <div className="md:hidden py-2 shrink-0">
          <div className="w-9 h-[5px] bg-black/[0.15] rounded-full mx-auto" />
        </div>
        {/* Mobile: iOS-style header (Cancelar/Atrás/Volver · título · X). */}
        <div className="md:hidden flex items-center justify-between px-4 pb-3 shrink-0">
          <div className="w-20 text-left">{leftButton}</div>
          <h3 className="text-[15px] font-semibold text-brand-black/90 truncate max-w-[55%] text-center">
            {headerTitle}
          </h3>
          <div className="w-20 flex justify-end">
            {(view === 'list' || view === 'multi') && !isMultiEditing && (
              <button
                type="button"
                onClick={onClose}
                className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
                aria-label="Cerrar"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
        {/* Desktop: title left · X close right · divider. Cuando es
            multi-editing o sub-view, agregamos un botón "Atrás/Volver"
            chiquito a la izquierda del título. */}
        <div className="hidden md:flex items-start justify-between px-5 py-4 shrink-0">
          <div className="flex items-start gap-3 min-w-0">
            {isMultiEditing ? (
              <button
                type="button"
                onClick={() => setEditingMultiIdx(null)}
                className="inline-flex items-center gap-0.5 text-brand-primary text-[14px] font-medium hover:opacity-75 transition-opacity mt-0.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Atrás
              </button>
            ) : isMultiSubFlow ? (
              <button
                type="button"
                onClick={() => setView('multi')}
                className="inline-flex items-center gap-0.5 text-brand-primary text-[14px] font-medium hover:opacity-75 transition-opacity mt-0.5"
              >
                <ArrowLeft className="w-4 h-4" />
                Atrás
              </button>
            ) : null}
            <div className="min-w-0">
              <h3 className="text-[17px] font-semibold text-brand-black/90">
                {headerTitle}
              </h3>
              {view === 'list' && (
                <p className="text-[13px] text-[#3C3C43]/55 mt-0.5">
                  Elegí el modelo que va a lucir tus productos en todas las
                  imágenes del catálogo.
                </p>
              )}
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="w-8 h-8 inline-flex items-center justify-center rounded-full bg-black/[0.06] text-brand-black/40 hover:bg-black/[0.1] transition-colors"
            aria-label="Cerrar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Contenido. list/multi-containers NO scrollean vertical (el
            carousel scrollea horizontal, los botones están anclados al
            bottom; en multi los containers tampoco overflowean). Para
            create/upload/multi-editing sí hace falta scroll. */}
        {view === 'list' ? (
          <div className="flex-1 min-h-0 px-5 pt-2 pb-6 animate-mode-swap">
            <ListView
              selection={selection}
              models={userModels}
              variant="sheet"
              hideAuto={hideAuto}
              onSelectAuto={() => {
                onChange({ mode: 'auto' });
                onClose();
              }}
              onSelectPreset={(m) => {
                onChange({
                  mode: 'preset',
                  id: String(m.id),
                  name: m.nombre,
                  imageUrl: m.thumbnailUrl,
                });
                onClose();
              }}
              onTapCreate={() => setView('create')}
              onTapUpload={() => setView('upload')}
              onTapMulti={
                onMultiEnabledChange ? handleActivateMulti : undefined
              }
            />
          </div>
        ) : showMultiContainers ? (
          <div className="flex-1 overflow-y-auto px-5 pt-3 pb-6 animate-mode-swap">
            {/* Aviso (texto gris) arriba-centro: varios modelos consumen más.
                Solo mobile (md:hidden): en desktop el aviso va abajo-izquierda
                en la card de Modelo (slot `extra` de DesignModeloPanel). */}
            <p className="md:hidden text-center text-[12px] text-[#3C3C43]/60 leading-snug mb-3 px-2">
              Usar varios modelos consume más créditos.
            </p>
            <MultiView
              slots={allSlots}
              products={multiProducts}
              productsPerSlot={multiProductsPerSlot}
              onTapSlot={(idx) => setEditingMultiIdx(idx)}
              onRemoveSlot={handleRemoveSlot}
              onTapSlotProducts={onTapSlotProducts}
              onAddSlot={onChangeExtras ? handleAddSlot : undefined}
              maxSlots={MAX_MULTI_SLOTS}
              lockSlotCount={lockSlotCount}
            />
          </div>
        ) : isMultiEditing ? (
          <div className="flex-1 min-h-0 px-5 pt-2 pb-6 animate-mode-swap">
            <ListView
              selection={
                editingMultiIdx !== null ? allSlots[editingMultiIdx] : null
              }
              models={userModels}
              variant="sheet"
              onSelectAuto={subListHandlers.onSelectAuto}
              onSelectPreset={subListHandlers.onSelectPreset}
              onTapCreate={subListHandlers.onTapCreate}
              onTapUpload={subListHandlers.onTapUpload}
            />
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto px-5 pt-2 pb-6 md:pt-4 md:pb-0 animate-mode-swap">
            {view === 'create' && isMultiSubFlow && editingMultiIdx !== null && (
              <CreateView
                initial={
                  allSlots[editingMultiIdx]?.mode === 'custom'
                    ? (allSlots[editingMultiIdx] as {
                        fields: CustomModelFields;
                      }).fields
                    : undefined
                }
                userId={userId}
                companyId={companyId}
                onModelCreated={onModelCreated}
                onBack={() => setView('multi')}
                onConfirmModel={(model) => {
                  handleSelectForEditingSlot({
                    mode: 'preset',
                    id: String(model.id),
                    name: model.nombre,
                    imageUrl: model.thumbnailUrl,
                  });
                  setView('multi');
                }}
              />
            )}
            {view === 'create' && !isMultiSubFlow && (
              <CreateView
                initial={
                  selection?.mode === 'custom' ? selection.fields : undefined
                }
                userId={userId}
                companyId={companyId}
                onModelCreated={onModelCreated}
                onBack={onClose}
                onConfirmModel={(model) => {
                  onChange({
                    mode: 'preset',
                    id: String(model.id),
                    name: model.nombre,
                    imageUrl: model.thumbnailUrl,
                  });
                  onClose();
                }}
              />
            )}
            {view === 'upload' && isMultiSubFlow && (
              <UploadFlow
                userId={userId}
                companyId={companyId}
                onModelCreated={onModelCreated}
                onUtilizar={(model) => {
                  handleSelectForEditingSlot({
                    mode: 'preset',
                    id: String(model.id),
                    name: model.nombre,
                    imageUrl: model.thumbnailUrl,
                  });
                  setView('multi');
                }}
              />
            )}
            {view === 'upload' && !isMultiSubFlow && (
              <UploadFlow
                userId={userId}
                companyId={companyId}
                onModelCreated={onModelCreated}
                onUtilizar={(model) => {
                  onChange({
                    mode: 'preset',
                    id: String(model.id),
                    name: model.nombre,
                    imageUrl: model.thumbnailUrl,
                  });
                  onClose();
                }}
              />
            )}
          </div>
        )}
      </div>
    </>,
    document.body,
  );
};
